import { useState, useEffect } from "react";
import { Tooltip, CircularProgress } from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import {
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { mt } from "date-fns/locale";

const AreaPpmSummary = ({ userSites }) => {
  const [loading, setLoading] = useState(true);
  const [regionStats, setRegionStats] = useState([]);
  const userToken = localStorage.getItem("token");

  useEffect(() => {
    const fetchRegionTaskStats = async () => {
      try {
        setLoading(true);
        
        // First get all regions
        const regionsResponse = await axios.get(
          "https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/regions",
          { headers: { Authorization: `${userToken}` } }
        );
        const regions = regionsResponse.data;

        // Fetch stats for each region
        const regionStatsPromises = regions.map(async (region) => {
          // Get sites for this region
          const sitesResponse = await axios.get(
            `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/region/sites?region_id=${region.region_id}`,
            { headers: { Authorization: `${userToken}` } }
          );
          const regionSites = sitesResponse.data;

          // Get stats for all sites in this region
          let regionTotal = { overdue: 0, upcoming: 0, completed: 0 };
          let siteBreakdown = { overdue: [], upcoming: [], completed: [] };

          for (const site of regionSites) {
            const sectionsResponse = await axios.get(
              `https://5d7600h5g2.execute-api.eu-west-2.amazonaws.com/Dev/sections?site_id=${site.site_id}`,
              { headers: { Authorization: `${userToken}` } }
            );
            const sections = sectionsResponse.data;

            const siteStats = sections.reduce(
              (acc, section) => ({
                overdue: acc.overdue + (section.overdue_tasks || 0),
                upcoming: acc.upcoming + 
                  ((section.total_tasks || 0) - 
                   (section.completed_tasks || 0) - 
                   (section.overdue_tasks || 0)),
                completed: acc.completed + (section.completed_tasks || 0),
              }),
              { overdue: 0, upcoming: 0, completed: 0 }
            );

            if (siteStats.overdue > 0) {
              siteBreakdown.overdue.push(`${site.name}: ${siteStats.overdue}`);
            }
            if (siteStats.upcoming > 0) {
              siteBreakdown.upcoming.push(`${site.name}: ${siteStats.upcoming}`);
            }
            if (siteStats.completed > 0) {
              siteBreakdown.completed.push(`${site.name}: ${siteStats.completed}`);
            }

            regionTotal.overdue += siteStats.overdue;
            regionTotal.upcoming += siteStats.upcoming;
            regionTotal.completed += siteStats.completed;
          }

          return {
            regionId: region.region_id,
            regionName: region.name,
            stats: regionTotal,
            siteBreakdown
          };
        });

        const stats = await Promise.all(regionStatsPromises);
        setRegionStats(stats);
      } catch (error) {
        console.error("Error fetching region stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchRegionTaskStats();
  }, [userSites]);

  const statusData = [
    {
      type: "overdue",
      label: "Overdue",
      color: "#FF4D4F",
      bgColor: "rgba(255, 77, 79, 0.2)",
      icon: <ExclamationCircleOutlined />,
    },
    {
      type: "upcoming",
      label: "Upcoming",
      color: "#FFC53D",
      bgColor: "rgba(255, 197, 61, 0.2)",
      icon: <ClockCircleOutlined />,
    },
    {
      type: "completed",
      label: "Completed",
      color: "#73D13D",
      bgColor: "rgba(115, 209, 61, 0.2)",
      icon: <CheckCircleOutlined />,
    },
  ];

  if (loading) {
    return (
      <VuiBox display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </VuiBox>
    );
  }

return (
  <VuiBox display="flex" flexDirection="column" mt={2} pb={1}>
    {/* Header row: Region + Status Icons/Labels */}
    <VuiBox
      display="flex"
      alignItems="center"
      width="100%"
      mb={2}
      sx={{ columnGap: "32px" }}
    >
      <VuiBox width="20%" pt={4} justifyContent="center" alignItems="center">
        <VuiTypography variant="button" color="white" fontWeight="bold" >
          
        </VuiTypography>
      </VuiBox>

      {statusData.map(({ type, label, color, bgColor, icon }) => (
        <VuiBox key={type} textAlign="center" width="20%">
          <VuiBox
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              backgroundColor: bgColor,
              margin: "0 auto",
            }}
          >
            <VuiTypography variant="h6" color={color}>
              {icon}
            </VuiTypography>
          </VuiBox>
          <VuiTypography variant="button" color="white" mt={0.5}>
            {label}
          </VuiTypography>
        </VuiBox>
      ))}
    </VuiBox>

    {/* Region stats rows aligned under the header */}
    {regionStats.map((region) => (
      <VuiBox
        key={region.regionId}
        mb={2}
        display="flex"
        alignItems="center"
        sx={{ columnGap: "32px" }}
      >
        {/* Region name */}
        <VuiBox width="20%">
          <VuiTypography variant="button" color="white" fontWeight="bold">
            {region.regionName}
          </VuiTypography>
        </VuiBox>

        {/* Stats for each column */}
        {statusData.map(({ type, color, label }) => (
          <VuiBox key={type} width="20%" textAlign="center">
            <Tooltip
              title={
                region.siteBreakdown[type]?.length > 0 ? (
                  <div style={{ color: "white", display: "flex", flexDirection: "column" }}>
                    {region.siteBreakdown[type].map((item, index) => (
                      <div key={index} style={{ marginBottom: "2px" }}>{item}</div>
                    ))}
                  </div>
                ) : (
                  <div style={{ color: "white" }}>No {label.toLowerCase()} tasks</div>
                )
              }
              arrow
            >
              <VuiTypography variant="h5" fontWeight="medium" color={color}>
                {region.stats[type]}
              </VuiTypography>
            </Tooltip>
          </VuiBox>
        ))}
      </VuiBox>
    ))}
  </VuiBox>
);
};

export default AreaPpmSummary;